import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, type CardView } from '../api';

/**
 * Follow state shared by every place a card appears (board, search, modal).
 *
 * The server's `following`/`follow_id` is the starting point; a change made here
 * is kept as an override keyed by the card's subject, so the card and its open
 * details never disagree. Semantics are unchanged from the original card: news
 * follows a story, CrossFit follows the card, and stopping uses the follow id.
 */

interface FollowOverride {
  following: boolean;
  followId: string | null;
}

interface FollowCtx {
  overrides: Record<string, FollowOverride>;
  set: (subject: string, o: FollowOverride) => void;
}

const Ctx = createContext<FollowCtx | null>(null);

export function FollowProvider({ children }: { children: ReactNode }) {
  const [overrides, setOverrides] = useState<Record<string, FollowOverride>>({});
  const set = useCallback((subject: string, o: FollowOverride) => {
    setOverrides((prev) => ({ ...prev, [subject]: o }));
  }, []);
  const value = useMemo(() => ({ overrides, set }), [overrides, set]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useFollow(view: CardView) {
  const ctx = useContext(Ctx);
  const subject = view.card.subject_ref ?? null;
  const override = subject && ctx ? ctx.overrides[subject] : undefined;
  const following = override ? override.following : view.following;
  const followId = override ? override.followId : (view.follow_id ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const toggle = useCallback(async () => {
    if (!subject) return;
    setBusy(true);
    setError('');
    try {
      if (following && followId) {
        await api.stopFollow(followId);
        ctx?.set(subject, { following: false, followId: null });
      } else {
        const f = await api.createFollow({
          subject_type: view.card.domain === 'crossfit' ? 'card' : 'story',
          subject_id: subject,
          label: view.version.title,
        });
        ctx?.set(subject, { following: true, followId: f.id });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Aktion fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }, [ctx, followId, following, subject, view.card.domain, view.version.title]);

  return { canFollow: subject !== null, following, busy, error, toggle };
}

/**
 * "Meldung verfolgen" (ADR-032): a story follow watches this one story cluster
 * for material changes. It does not track a development over weeks; later
 * reporting that forms a new story is not part of it. Tracking a company,
 * authority or person across stories is a subject ("Subjekt verfolgen").
 */
export function FollowButton({ view, variant = 'text' }: { view: CardView; variant?: 'text' | 'button' }) {
  const f = useFollow(view);
  if (!f.canFollow) return null;
  const crossfit = view.card.domain === 'crossfit';
  const label = f.following
    ? crossfit
      ? 'Karte verfolgt ✓'
      : 'Meldung verfolgt ✓'
    : crossfit
      ? 'Diese Karte verfolgen +'
      : 'Meldung verfolgen +';
  return (
    <>
      <button
        type="button"
        className={variant === 'button' ? 'button' : 'text-button follow'}
        aria-pressed={f.following}
        title={crossfit ? undefined : 'Beobachtet diese eine Meldung auf wesentliche Änderungen. Eine spätere, neue Meldung zum selben Thema gehört nicht dazu.'}
        aria-label={f.following ? `Meldung nicht mehr verfolgen: ${view.version.title}` : `Meldung verfolgen: ${view.version.title}`}
        disabled={f.busy}
        onClick={f.toggle}
      >
        {f.busy ? 'Speichert…' : label}
      </button>
      {f.error && (
        <span className="inline-error" role="alert">
          {f.error}
        </span>
      )}
    </>
  );
}

/** The reader's tracked subjects found in this story, linked to their pages. */
export function SubjectChips({ view }: { view: CardView }) {
  const list = view.subjects ?? [];
  if (list.length === 0) return null;
  return (
    <span className="subject-chips">
      {list.map((s) => (
        <Link
          key={s.entity_id}
          className={`tag tag-subject${s.role === 'main' ? ' is-main' : ''}`}
          to={`/subjekte/${s.entity_id}`}
          title={s.role === 'main' ? 'Dein Subjekt ist Hauptakteur dieser Meldung' : 'Dein Subjekt wird hier nur erwähnt'}
        >
          Subjekt: {s.name}
          <span className="visually-hidden">{s.role === 'main' ? ' (Hauptakteur)' : ' (erwähnt)'}</span>
          {s.role === 'mention' && <span aria-hidden="true"> · erwähnt</span>}
        </Link>
      ))}
    </span>
  );
}
